/**
 * Scoring Step Component
 *
 * Step 2 of the assessment wizard - score each SOP step
 * Features:
 * - Hierarchical display (Jobs → Tasks → Steps)
 * - Progress bar showing scored steps
 * - Batch scoring actions
 * - Collapsible sections
 */

import { useState, useCallback, useMemo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  ShieldAlert,
  ListChecks,
  Zap,
  Target,
} from 'lucide-react';
import { StepScoreCard } from './StepScoreCard';
import type { SopDetail, SopTaskStep } from '@guild/shared';
import type { StepScore } from '../NewAssessmentPage';

interface ScoringStepProps {
  sop: SopDetail;
  stepScores: Record<string, StepScore>;
  allSteps: Array<SopTaskStep & { sectionTitle: string; taskTitle: string; isSafetyCritical: boolean }>;
  onStepScoreChange: (stepId: string, score: StepScore) => void;
  onBatchScore: (stepIds: string[], score: number) => void;
}

export function ScoringStep({
  sop,
  stepScores,
  allSteps,
  onStepScoreChange,
  onBatchScore,
}: ScoringStepProps) {
  // Track expanded sections and tasks
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    // Start with first section expanded
    const firstSection = sop.sections?.[0];
    return firstSection ? new Set([firstSection.id]) : new Set();
  });
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  // Calculate progress
  const scoredCount = Object.keys(stepScores).length;
  const totalCount = allSteps.length;
  const progressPercent = totalCount > 0 ? Math.round((scoredCount / totalCount) * 100) : 0;

  // Get unscored step IDs
  const unscoredStepIds = useMemo(() => {
    return allSteps
      .filter(step => !stepScores[step.id])
      .map(step => step.id);
  }, [allSteps, stepScores]);

  // Toggle handlers
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const toggleTask = useCallback((taskId: string) => {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    if (sop.sections) {
      setExpandedSections(new Set(sop.sections.map(s => s.id)));
      const allTaskIds = sop.sections.flatMap(s => s.tasks?.map(t => t.id) || []);
      setExpandedTasks(new Set(allTaskIds));
    }
  }, [sop.sections]);

  const collapseAll = useCallback(() => {
    setExpandedSections(new Set());
    setExpandedTasks(new Set());
  }, []);

  // Batch scoring handlers
  const handleMarkRemainingPass = () => {
    if (unscoredStepIds.length > 0) {
      onBatchScore(unscoredStepIds, 80);
    }
  };

  const handleMarkAllPass = () => {
    const allIds = allSteps.map(s => s.id);
    onBatchScore(allIds, 80);
  };

  return (
    <div className="animate-in fade-in duration-300">
      {/* Header with progress */}
      <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Score Each Step</h2>
            <p className="text-sm text-gray-500 mt-1">
              Evaluate operator performance for each procedural step
            </p>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#00674a] rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-sm font-semibold text-gray-700 min-w-[80px]">
                {scoredCount} / {totalCount}
              </span>
            </div>

            {progressPercent === 100 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold animate-in zoom-in duration-300">
                <CheckCircle2 className="w-4 h-4" />
                Complete
              </span>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <button
            onClick={expandAll}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-[#00674a] hover:bg-[#c5e8c2]/20 rounded-lg transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-[#00674a] hover:bg-[#c5e8c2]/20 rounded-lg transition-colors"
          >
            Collapse All
          </button>
          <div className="w-px h-4 bg-gray-200 mx-1" />
          <button
            onClick={handleMarkRemainingPass}
            disabled={unscoredStepIds.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#c5e8c2]/30 text-[#00674a] hover:bg-[#c5e8c2]/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Zap className="w-3.5 h-3.5" />
            Mark Remaining Pass (80)
          </button>
          <button
            onClick={handleMarkAllPass}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#00674a]/10 text-[#00674a] hover:bg-[#00674a]/20 rounded-lg transition-colors"
          >
            <Target className="w-3.5 h-3.5" />
            Mark All Pass (80)
          </button>
        </div>
      </div>

      {/* Hierarchical content */}
      <div className="p-6 space-y-4 max-h-[calc(100vh-28rem)] overflow-y-auto">
        {sop.sections?.map((section, sectionIndex) => {
          const isSectionExpanded = expandedSections.has(section.id);
          const sectionSteps = allSteps.filter(s => s.sectionTitle === section.title);
          const sectionScoredCount = sectionSteps.filter(s => stepScores[s.id]).length;
          const hasSafetyTasks = section.tasks?.some(t => t.isSafetyCritical) ?? false;

          return (
            <div
              key={section.id}
              className="border border-gray-200 rounded-xl overflow-hidden transition-all hover:border-[#00674a]/30"
            >
              {/* Section header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-4 py-4 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center gap-3 text-left"
              >
                <div className="text-gray-400">
                  {isSectionExpanded ? (
                    <ChevronDown className="w-5 h-5" />
                  ) : (
                    <ChevronRight className="w-5 h-5" />
                  )}
                </div>

                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#00674a] text-white flex items-center justify-center font-bold text-sm shadow-sm">
                  {sectionIndex + 1}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{section.title}</h3>
                  {section.description && (
                    <p className="text-xs text-gray-500 truncate mt-0.5">{section.description}</p>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {hasSafetyTasks && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs font-medium">
                      <ShieldAlert className="w-3 h-3" />
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-600">
                    <ListChecks className="w-3.5 h-3.5 text-gray-400" />
                    {sectionScoredCount}/{sectionSteps.length}
                  </span>
                </div>
              </button>

              {/* Section tasks */}
              {isSectionExpanded && section.tasks && section.tasks.length > 0 && (
                <div className="border-t border-gray-100">
                  {section.tasks.map((task, taskIndex) => {
                    const isTaskExpanded = expandedTasks.has(task.id);
                    const taskSteps = task.steps || [];
                    const taskScoredCount = taskSteps.filter(s => stepScores[s.id]).length;

                    return (
                      <div key={task.id} className="border-b border-gray-50 last:border-b-0">
                        {/* Task header */}
                        <button
                          onClick={() => toggleTask(task.id)}
                          className="w-full px-4 py-3 hover:bg-gray-50/50 transition-colors flex items-center gap-3 text-left"
                        >
                          <div className="flex items-center gap-2 pl-6">
                            <div className="text-gray-300">
                              {taskSteps.length > 0 ? (
                                isTaskExpanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )
                              ) : (
                                <div className="w-4 h-4" />
                              )}
                            </div>
                          </div>

                          <div className="flex-shrink-0 w-6 h-6 rounded bg-gray-100 text-gray-600 flex items-center justify-center font-semibold text-xs">
                            {taskIndex + 1}
                          </div>

                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-800">{task.title}</span>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            {task.isSafetyCritical && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-xs font-medium">
                                <ShieldAlert className="w-3 h-3" />
                                Safety
                              </span>
                            )}
                            {taskSteps.length > 0 && (
                              <span className={`text-xs font-semibold ${
                                taskScoredCount === taskSteps.length
                                  ? 'text-emerald-600'
                                  : 'text-gray-400'
                              }`}>
                                {taskScoredCount}/{taskSteps.length}
                              </span>
                            )}
                          </div>
                        </button>

                        {/* Task steps */}
                        {isTaskExpanded && taskSteps.length > 0 && (
                          <div className="pb-4 pl-20 pr-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                            {taskSteps.map((step, stepIndex) => (
                              <StepScoreCard
                                key={step.id}
                                stepId={step.id}
                                stepNumber={stepIndex + 1}
                                description={step.description}
                                isSafetyCritical={step.isSafetyCritical || task.isSafetyCritical}
                                score={stepScores[step.id]}
                                onChange={onStepScoreChange}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Empty state */}
        {(!sop.sections || sop.sections.length === 0) && (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <ListChecks className="w-8 h-8 text-amber-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">No Steps Found</h3>
            <p className="text-gray-500 max-w-sm mx-auto">
              This SOP doesn't have any steps to score. Please select a different SOP.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
